import React, { ReactNode } from "react";
import FocusTrap from "focus-trap-react";
import { Button } from "../Button";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils/StringUtils";
import { ModalContainer, ModalBackdrop } from "../../Styles/Modal/Containers";

export interface Props {
  id: string;
  title: string;
  isOpen: boolean;
  modalButtons: React.ComponentProps<typeof Button>[];
  children?: ReactNode;
  dataTestId?: string;
  classes?: string;
  alignButtons?: string | "right";
}

export const Modal = (props: Props) => {
  const classList = {
    defaultClass: "ons-modal",
    optionalClasses: [props.classes ? props.classes : null],
  };

  const className = concatStringFromClassesArrayList(classList);

  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const modalButtons = props.modalButtons.map((modalButton) => {
    const sanitisedButtonText = sanitiseString(modalButton.text);
    return (
      <Button
        id={`${sanitisedId}-button-${sanitisedButtonText}`}
        key={`${sanitisedId}-button-${sanitisedButtonText}`}
        dataTestId={`${sanitisedDataTestId}-button-${sanitisedButtonText}`}
        text={modalButton.text}
        variants={modalButton.variants}
        classes={modalButton.classes}
        onClick={modalButton.onClick}
      />
    );
  });

  return (
    props.isOpen && (
      <ModalBackdrop>
        <FocusTrap
          focusTrapOptions={{
            initialFocus: false,
            tabbableOptions: { displayCheck: "none" },
          }}
        >
          <ModalContainer
            id={sanitisedId}
            data-testid={`${sanitisedDataTestId}-modal-container`}
            className={className}
            aria-labelledby="ons-modal-title"
          >
            <div className="ons-modal__content">
              <h2 id="ons-modal-title" className="ons-modal__title ons-u-mb-l">
                {props.title}
              </h2>
              <div className="ons-u-mb-l">{props.children}</div>
            </div>
            <div
              data-testid={`${sanitiseString(
                props.dataTestId
              )}-button-container`}
              className={`${
                props.alignButtons === "left" ? "" : "ons-grid--reverse"
              }`}
            >
              {modalButtons.map((modalButton, index) => {
                const buttonClasses =
                  props.alignButtons !== "left"
                    ? "ons-u-ml-s ons-u-mr-no"
                    : "ons-u-mr-s ons-u-ml-no";

                const buttonClassList = {
                  optionalClasses: [
                    buttonClasses,
                    modalButton.props.classes || null,
                  ],
                };

                const buttonClassName =
                  concatStringFromClassesArrayList(buttonClassList);

                const button = {
                  ...modalButton,
                  props: { ...modalButton.props, classes: buttonClassName },
                };

                return <React.Fragment key={index}>{button}</React.Fragment>;
              })}
            </div>
          </ModalContainer>
        </FocusTrap>
      </ModalBackdrop>
    )
  );
};
